import {css, useTheme} from '@mui/material';
import {
  asQueryString,
  ConfigObjectType,
  DlMuiButton,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightSplitLayout,
  LoadingSpinner,
  mixins,
  omitNullsy,
  useDefaultScrollTo,
  useLocalStorage,
  useLocationQuery,
} from '@wallaby-ui/core';
import {LightSelectedFilters, useFormatters, usePermissions, UserFilterDto, useUserFilters} from '@wallaby-ui/document';
import {isEqual} from 'lodash';
import * as React from 'react';
import {useCallback, useMemo} from 'react';
import {Helmet} from 'react-helmet';
import {Link} from 'react-router-dom';

import {useUzisReportData} from './useUzisReportData';
import {useUzisReportExport} from './useUzisReportExport';
import {normalizeUzisReportFiltersForCompare, UzisReportFilters} from './UzisReportFilters';
import {UzisReportTableV} from './UzisReportTableV';
import {UzisReportTableVI} from './UzisReportTableVI';
import {UzisReportTableVII} from './UzisReportTableVII';
import {UzisReportTableVIII} from './UzisReportTableVIII';
import {
  DEFAULT_UZIS_REPORT_FILTERS,
  FiltersData,
  getUzisReportLayoutId,
  UzisReportFilter,
  UZIS_REPORT_LAYOUT_SPLIT_CONFIG,
} from './uzisreporttypes';
import {useHospitalOptions} from '../meriehospitalreports/useHospitalOptions';

type Props = {
  config: ConfigObjectType;
};

export const MerieUzisReportLayout: React.FC<Props> = (props) => {
  const {config} = props;
  const storageIdPrefix = '/merieUzisReportLayout';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);

  const defaultReportFilters: FiltersData['report'] = useMemo(() => {
    return DEFAULT_UZIS_REPORT_FILTERS();
  }, []);

  const filters: FiltersData = useMemo(
    () => ({
      report: query?.report ?? defaultReportFilters,
      savedFiltersOpen: query?.savedFiltersOpen ?? true,
    }),
    [query, defaultReportFilters]
  );

  const changeReportFilters = useCallback((newFilters) => changeFilters({report: newFilters}), [changeFilters]);

  const component = 'uzisReport';
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: changeReportFilters,
  });

  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;

  return (
    <MerieUzisReportLayoutComponents
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
      defaultReportFilters={defaultReportFilters}
    />
  );
};

type MerieUzisReportLayoutComponentsProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Partial<FiltersData>) => void;
  defaultReportFilters?: FiltersData['report'];
};

const MerieUzisReportLayoutComponents: React.FC<MerieUzisReportLayoutComponentsProps> = ({
  config,
  storageIdPrefix,
  component,
  userFiltersLoading,
  addedUserFilters,
  userFilters,
  changeUserFilter,
  filters,
  changeFilters,
  defaultReportFilters,
}) => {
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const {isLoading: permissionsLoading} = usePermissions(config);
  const {t} = useFormatters();

  const changeFiltersRef = React.useRef(changeFilters);
  React.useEffect(() => {
    changeFiltersRef.current = changeFilters;
  }, [changeFilters]);

  const reportUrl = `${config.service.url.uzisReport}`;
  const {reportData} = useUzisReportData({
    reportUrl,
    filters: filters.report,
  });

  const {handleXlsxExport} = useUzisReportExport(filters.report, config);

  const searchQueryParams = useMemo(() => {
    return {
      recommendation: {
        pharmaRecommendationDateFrom: filters.report.dateFrom,
        pharmaRecommendationDateTo: filters.report.dateTo,
        pharmaRecommendationOrganizationIds: filters.report.organizationIds,
      },
    };
  }, [filters.report.dateFrom, filters.report.dateTo, filters.report.organizationIds]);

  const handleFiltersChange = useCallback(
    (newFilters: Partial<UzisReportFilter>) => {
      changeFiltersRef.current?.({
        report: {...filters.report, ...newFilters},
      });
    },
    [filters.report]
  );

  const onToggleSavedFiltersOpen = useCallback(() => {
    changeFiltersRef.current?.({savedFiltersOpen: !filters.savedFiltersOpen});
  }, [filters.savedFiltersOpen]);

  const handleResetFilters = useCallback(() => {
    changeFiltersRef.current?.({report: defaultReportFilters});
  }, [defaultReportFilters]);

  const handleSetFilter = useCallback((newFilter: UzisReportFilter) => {
    changeFiltersRef.current?.({report: newFilter});
  }, []);

  const hasFilters = !isEqual(
    normalizeUzisReportFiltersForCompare(omitNullsy(filters.report) as any),
    normalizeUzisReportFiltersForCompare(omitNullsy(defaultReportFilters) as any)
  );
  const mainSplitLayout = UZIS_REPORT_LAYOUT_SPLIT_CONFIG(getUzisReportLayoutId());

  const [defaultScroll, setDefaultScroll] = useLocalStorage(`${storageIdPrefix}.scroll`, 0);
  const scrollWrapperRef = useDefaultScrollTo(defaultScroll);
  const onScroll = useCallback(
    (event: any) => {
      setDefaultScroll((event.target as HTMLElement).scrollTop);
    },
    [setDefaultScroll]
  );

  if (permissionsLoading) {
    return <LoadingSpinner isVisible text='' />;
  }

  return (
    <div css={muiStyles.contentWrapper} style={{overflowY: 'auto'}} ref={scrollWrapperRef} onScroll={onScroll}>
      <Helmet title={`${t('reports.list.pageTitle')} | ${t('navigation.route.uzisReportMerie')}`} />
      <LightSplitLayout
        initSizes={mainSplitLayout[0].sizes}
        minSizes={mainSplitLayout[0].minSize}
        storageId={mainSplitLayout[0].layoutId}
        isVertical
        gutterSize={18}
        noGrow={[!filters.savedFiltersOpen, false, false, false, false]}
        autoFitFilterRows={{enabled: filters.savedFiltersOpen}}
        hidden={[false, false, false, false, false]}
        style={{minHeight: '2000px'}}
      >
        <div data-name='report-filters' css={muiStyles.filtersContentBox}>
          <UzisReportFilters
            filters={filters.report}
            onFiltersChange={handleFiltersChange}
            savedFiltersOpen={filters.savedFiltersOpen}
            onToggleSavedFilters={onToggleSavedFiltersOpen}
            storageIdPrefix={storageIdPrefix}
            component={component}
            userFiltersLoading={userFiltersLoading}
            addedUserFilters={addedUserFilters}
            userFilters={userFilters}
            changeUserFilter={changeUserFilter}
            config={config}
            onSetFilter={handleSetFilter}
            defaultReportFilters={defaultReportFilters}
            hasActiveFilter={hasFilters}
          />
        </div>

        <div data-name='report-header' css={muiStyles.contentBox}>
          <IconHeaderWithSearch
            iconName='listIconMultiColor'
            label='reports.uzis.tableV.title'
            useFixedViewBox='0 -1 16 14'
            iconCss={muiStyles.headerIcon}
            childrenWrapperStyle={{gap: 6}}
          >
            <LightSelectedFilters
              align='flex-start'
              marginLeft='8px'
              hasFilters={hasFilters}
              handleResetFilters={handleResetFilters}
              hasXlsxExport
              handleXlsxExport={handleXlsxExport}
            />
            <Link css={muiStyles.showAllLink} tabIndex={0} to={`/pharmacotherapyRecommendationList?${asQueryString(searchQueryParams)}`}>
              <DlMuiButton
                type='chip-filled'
                title='dashboard.showAll'
                onClick={() => {
                  // noop
                }}
              />
            </Link>
          </IconHeaderWithSearch>
          <div data-name='table-v' css={muiStyles.tableBox}>
            <UzisReportTableV data={reportData?.tableV} isLoading={reportData?.tableVLoading} storageIdPrefix={storageIdPrefix} />
          </div>
        </div>

        <div data-name='table-vi' css={muiStyles.tableBox}>
          <UzisReportTableVI data={reportData?.tableVI} isLoading={reportData?.tableVILoading} storageIdPrefix={storageIdPrefix} />
        </div>

        <div data-name='table-vii' css={muiStyles.tableBox}>
          <UzisReportTableVII data={reportData?.tableVII} isLoading={reportData?.tableVIILoading} storageIdPrefix={storageIdPrefix} />
        </div>

        <div data-name='table-viii' css={muiStyles.tableBox}>
          <UzisReportTableVIII data={reportData?.tableVIII} isLoading={reportData?.tableVIIILoading} storageIdPrefix={storageIdPrefix} />
        </div>
      </LightSplitLayout>
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
  `,
  contentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    background: #ffffff;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,
  /* Filters card only: no bottom padding, so the split's autoFit peek/show-all is clipped right AT the
     divider instead of 16px above it (the padding pushed the clip line up). */
  filtersContentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    background: #ffffff;
    padding: 16px 16px 0 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,
  tableBox: css`
    ${mixins.contentBox};
    padding: 0;
    overflow-y: auto;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  showAllLink: css`
    text-decoration: none;
    margin-left: auto;
  `,
});
